import React, { useRef, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import API_BASE from '../../utils/api.js';
import { authFetch } from '../utils/authFetch';
import notify from '../../utils/notify';

function formatFileSize(bytes) {
  const n = Number(bytes) || 0;
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

export default function TodoAttachments({ form, setForm }) {
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);
  const attachments = Array.isArray(form.attachments) ? form.attachments : [];

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;

    try {
      setUploading(true);
      const signRes = await authFetch(
        `${API_BASE}/api/upload/presigned-url?filename=${encodeURIComponent(
          file.name
        )}&type=${encodeURIComponent(file.type || 'application/octet-stream')}&size=${encodeURIComponent(
          file.size
        )}`
      );
      const sign = await signRes.json();
      if (!signRes.ok) throw new Error(sign.error || 'Failed to get upload URL');

      const uploadRes = await fetch(sign.uploadUrl, {
        method: 'PUT',
        headers: { 'Content-Type': file.type || 'application/octet-stream' },
        body: file,
      });
      if (!uploadRes.ok) throw new Error('Upload failed');

      const attachment = {
        id: uuidv4(),
        name: file.name,
        url: sign.fileUrl,
        size: file.size,
        contentType: file.type || 'application/octet-stream',
        uploadedAt: new Date().toISOString(),
      };
      setForm((f) => ({
        ...f,
        attachments: [...(Array.isArray(f.attachments) ? f.attachments : []), attachment],
      }));
    } catch (err) {
      console.error('Attachment upload error', err);
      notify(err.message || 'Failed to upload attachment');
    } finally {
      setUploading(false);
    }
  };

  const removeAttachment = (id) => {
    setForm((f) => ({
      ...f,
      attachments: (Array.isArray(f.attachments) ? f.attachments : []).filter((a) => a.id !== id),
    }));
  };

  return (
    <div className="todo-attachments-field">
      <span className="todo-attachments-label">Attachments</span>

      {attachments.length > 0 && (
        <div className="todo-attachments-list">
          {attachments.map((a) => (
            <div key={a.id} className="todo-attachment-row">
              <a href={a.url} target="_blank" rel="noreferrer" className="todo-attachment-name">
                {a.name}
              </a>
              <span className="todo-attachment-size">{formatFileSize(a.size)}</span>
              <button
                type="button"
                className="todo-attachment-remove"
                onClick={() => removeAttachment(a.id)}
                aria-label={`Remove ${a.name}`}
              >
                ×
              </button>
            </div>
          ))}
        </div>
      )}

      <div>
        <input ref={fileInputRef} type="file" style={{ display: 'none' }} onChange={handleFileChange} />
        <button
          type="button"
          className="link-btn todo-attachment-add"
          onClick={() => fileInputRef.current?.click()}
          disabled={uploading}
        >
          {uploading ? 'Uploading…' : '+ Add attachment'}
        </button>
      </div>
    </div>
  );
}
